// @flow
import React from 'react';
import { withRouter } from 'react-router-dom';
import type { RouterHistory } from 'react-router-dom';
import ZingTouch from 'zingtouch';

import { withTheme, withStyles } from '@material-ui/core/styles';
import Fab from '@material-ui/core/Fab';
import CircularProgress from '@material-ui/core/CircularProgress';
import Send from '@material-ui/icons/Send';
import Input from '@material-ui/core/Input';
import Slide from '@material-ui/core/Slide';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { Typography, Tooltip } from '@material-ui/core';
import Toggle from 'react-toggled';
import { withTranslation } from 'react-i18next';

import Close from '@material-ui/icons/Close';
import ExpandIcon from '../../../assets/icons/expand.svg';
import AudioChunkBufferContainer from './AudioChunkBufferContainer';
import AudioRecorderContainer from './AudioRecorderContainer';
import ScribeConversationOverModalContainer from './ScribeConversationOverModalContainer';
import ScribeFooterMenu from './ScribeFooterMenuContainer';
import ScribeKeypressHandlerContainer from './ScribeKeypressHandlerContainer';
import ScribeSearchInputContainer from './ScribeSearchInputContainer';
import ScribeTranscriptListContainer from './ScribeTranscriptListContainer';
import Offline from '../../components/Offline';
import EditProfileModal from './EditProfileModal';
import ZoomBroadcastCaptionsPopupContainer from './ZoomBroadcastCaptionsPopupContainer';
import DesktopAppCaptionsModal from './DesktopAppCaptionsModal';

import getSocket from '../../utils/getSocket';
import * as segment from '../../utils/segment';
import UserSearchContainer from '../../components/UserSearchContainer';
import { fetchWithToken, getAvaBackendEndpoint, clearSearchValue } from '../../utils/http';
import {
  sendConnectionRequestMessage,
  getDefaultRoomId,
  isConversationEnded,
  sendResetRoomIdMessage,
  sendRoomStateUpdateMessage,
  sendUsePaidAsrUpdateMessage,
  isDefaultRoomId,
} from '../../utils/ws-v1';
import ConversationRequestMeta from '../../components/ConversationRequestMeta';
import DesktopAppInstalledComponent from '../../components/DesktopAppInstalledComponent';
import WebConversationOverModalContainer from './WebConversationOverModalContainer';
import Loading from '../../components/Loading';
import SwitchFooterContainer from '../../components/SwitchFooterContainer';
import { getSnack } from '../../utils/snackbar';
import LightViewFooter from './LightViewFooter';
import { log } from '../../utils/log';
import Keyboard from '../../../assets/images/keyboard.png';
import { terminateCall } from '../../utils/calls';
import { isMobile } from '../../utils';

import { SiteThemeContext } from '../../contexts/SiteThemeProvider';
import SnackbarWithCountdown from '../../components/SnackbarWithCountdown';
import electron from '../../utils/electron';

const branchSdk = require('branch-sdk');

const fabStyle = theme => ({
  position: 'absolute',
  top: `calc(100% - 80px)`,
  right: 100,
  backgroundColor: theme.palette.ava.blue,
  color: 'white',
  zIndex: 100,
});

const styles = theme => ({
  electronCaptionModeButtons: {
    background: '#222222',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'absolute',
    right: 0,
    transition: 'opacity 200ms',
    top: 0,
    width: 48,
    WebkitAppRegion: 'no-drag',
  },
  electronCaptionModeButton: {
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: '#3A3A3A',
    },
    alignItems: 'center',
    backgroundColor: '#222222',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    transition: 'background-color 200ms',
    width: '100%',
    WebkitAppearance: 'none',
  },
  zoomBroadcastWarningDialog: {
    maxWidth: 500,
    margin: '0 auto',
  },
  zoomBroadcastWarningIconWrap: {
    alignItems: 'center',
    background: theme.palette.ava.blue,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    height: 120,
    margin: '12px auto',
    width: 120,
  },
  zoomBroadcastWarningIconText: {
    fontSize: 72,
    color: theme.palette.background.light,
    marginRight: 5,
  },
  zoomBroadcastWarningTitle: {
    fontWeight: 700,
  },
  zoomBroadcastWarningDialogActions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  zoomBroadcastWarningButton: {
    '&:hover': {
      background: theme.palette.ava.blue,
      opacity: 0.9,
    },
    background: theme.palette.ava.blue,
    color: theme.palette.background.light,
    fontSize: '1.2em',
    fontWeight: 700,
    margin: '0 auto 8px',
    padding: '0.4em 1.6em',
    textTransform: 'none',
  },
  zoomBroadcastWarningCancel: {
    '&:hover': {
      background: 'none',
      textDecoration: 'underline',
    },
    color: theme.palette.ava.blue,
    fontSize: '1.2em',
    fontWeight: 700,
    marginBottom: '12px',
    marginRight: 8,
    padding: '0.4em 1.6em',
    textTransform: 'none',
  },
});

type Props = {
  acceptScribeConversationRequest: Function,
  activeFeatures: Array<string>,
  backendSupport: Array<string>,
  branch: Object,
  broadcastToZoomToken?: string,
  classes: Object,
  clearScribeConversationState: Function,
  clearScribeSessionState: Function,
  conversationEnded: boolean,
  conversationOrigin?: string,
  conversationOrigin?: string,
  conversationRequested?: boolean,
  conversationRequested?: boolean,
  conversationStarted?: boolean,
  conversationStarted?: boolean,
  createNewConversation: Function,
  currentPlatform: string,
  displayable: any,
  electronCaptionMode: boolean,
  exitScribeMode: Function,
  fontSize: number,
  fullScreen: boolean,
  getTranscriptText: Function,
  getTranscriptStats: Function,
  haveTranscripts: boolean,
  history: RouterHistory,
  inTwilioCalls: Boolean,
  inConferenceCall: Boolean,
  insertSpeakerNameMode: Boolean,
  intercomDisplayed: boolean,
  isInConversation: boolean,
  i18n: Object,
  lastMutationReceived: number,
  lightView: boolean,
  loading?: boolean,
  mirror: boolean,
  muted: boolean,
  newTranscriptInputVisibility: boolean,
  reconnectToWebSocket: Function,
  recording: boolean,
  scribe: boolean,
  scribeCreateTranscript: Function,
  scribeDeselectTranscriptWord: Function,
  socketConnected: string,
  startRecord: Function,
  stopRecord: Function,
  status: Object,
  switchPlatform: Function,
  switchWebProduct: Function,
  subscription: Object,
  t: Function,
  theme: Object,
  toggleSideBar: Function,
  token: string,
  updateAudioSourceMode: boolean,
  updateBroadcastToZoomToken: Function,
  updateConversationEndedStatus: Function,
  updateFullScreen: Function,
  updateElectronCaptionMode: Function,
  updateIsInConversation: Function,
  updateNewTranscriptInputVisibility: Function,
  user: Object,
};

type State = {
  hasConversationStarted: boolean,
  waitForConnectResponse: boolean,
  askingUserName: boolean,
  avaNameRequested?: string,
  newTranscriptDraft: string,
  requestedMeta?: Object,
  zoomExportPopup?: string,
  previousWindowBounds: {
    x: number,
    y: number,
    width: number,
    height: number,
  },
  previousDarkMode: boolean,
  showConversationEndModal: boolean,
  showZoomBroadcastingWarning: boolean,
  broadcastToZoomWarningShown: boolean,
  themeHasSwitched: boolean,
  roomEndingWarningCountdown: number,
  roomEndingWarningBegin: number,
  roomEndingWarningProlongUrl: string,
  checkIfDesktopAppInstalled: boolean,
  showDesktopAppCaptionPopup: boolean,
  conversationBroadcastedToZoom: boolean,
};

class ScribeConversation extends React.Component<Props, State> {
  audioBuffer;

  silenceTimeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasConversationStarted: false,
      waitForConnectResponse: false,
      askingUserName: false,
      newTranscriptDraft: '',
      requestedMeta: undefined,
      previousWindowBounds: {
        x: 100,
        y: 100,
        width: 800,
        height: 600,
      },
      previousDarkMode: false,
      showConversationEndModal: false,
      showZoomBroadcastingWarning: false,
      broadcastToZoomWarningShown: false,
      themeHasSwitched: true,
      roomEndingWarningBegin: 0,
      roomEndingWarningCountdown: 0,
      roomEndingWarningProlongUrl: '',
      checkIfDesktopAppInstalled: false,
      showDesktopAppCaptionPopup: false,
      conversationBroadcastedToZoom: false,
    };
  }

  onAvaName = async (avaNameArg: string, meta?: Object) => {
    let avaName = avaNameArg;
    if (!avaName) {
      ({ avaNameRequested: avaName } = this.state);
    }
    const { user } = this.props;
    const { askingUserName, requestedMeta } = this.state;
    if (!meta) {
      // eslint-disable-next-line
      meta = requestedMeta;
      this.setState({ requestedMeta: undefined });
    }

    if (user.userName === 'web guest' && !meta && askingUserName) {
      this.setState({ askingUserName: false });
      return;
    }
    if (user.avaName === avaName && avaName) {
      this.setState({ askingUserName: false });
      const { switchPlatform, switchWebProduct, history } = this.props;
      switchWebProduct('mirror');
      await switchPlatform('web', history);
      return;
    }
    this.setState({ waitForConnectResponse: true, askingUserName: false });
    const socket = await getSocket();
    const id = sendConnectionRequestMessage(socket, { avaName }, meta);
    let gotConnectionResponse = false;
    const handleConnectResponse = message => {
      const data = JSON.parse(message.data);
      const stopListening = () => {
        socket.removeEventListener('message', handleConnectResponse);
      };
      if (data.type === 'connection-response' && data.id === id) {
        log('websocketMessage', 'info', {
          message: data,
          direction: 'b2m',
        });
        gotConnectionResponse = true;
        this.handleConnectResponse(data, avaName, meta, stopListening);
      } else if (data.type === 'room-status' && gotConnectionResponse) {
        stopListening();
        // This user just joined a conversation
        segment.track('Joined Conversation', {
          HostAvaName: data.host.avaName,
          HostUserId: data.host.avaId,
          'Join Method': meta && meta.viaBranch ? 'Share Link': 'Connect Request',
        });
        const metricsToUpdate = {
          'Total Joins': 1,
          'Total Invites Connected Count': meta && meta.viaBranch ? 1 : 0,
        };
        fetchWithToken(
          `${getAvaBackendEndpoint()}/api/v1.1/users/${
            user.avaId
          }?firebaseAuthUID=${user.firebaseAuthUID || user.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              joinMetrics: metricsToUpdate,
            }),
          }
        );
      }
    };
    socket.addEventListener('message', handleConnectResponse);
  };

  handleEditProfilModalClose({ updated }) {
    if (updated) {
      this.onAvaName('');
    } else {
      this.setState({
        askingUserName: false,
      });
    }
  }

  handleConnectResponse(data: any, avaName?: string, meta: Object, stopListening: function) {
    const { t } = this.props;
    this.setState({ waitForConnectResponse: false });
    if (data.value) {
      return;
    }
    // There was a problem, stop listening to socket
    stopListening();
    if (data.reason === 'mustUpdateUsername') {
      this.setState({
        askingUserName: true,
        requestedMeta: meta,
        avaNameRequested: avaName,
      });
      return;
    }
    getSnack(this.props)(t(`snackbar.request.${data.reason}`), {
      variant: 'error',
    });
  }

  stopCallsIfNeeded = async () => {
    const { status, user } = this.props;
    Object.keys(status.twilioCalls || {}).forEach(sid => {
      terminateCall(user, sid);
    });
  };

  restartConversation = async () => {
    const { user, exitScribeMode, status } = this.props;
    if (status.host && status.host.avaId === user.avaId) {
      this.stopCallsIfNeeded();
    }
    clearSearchValue(window, 'token');
    exitScribeMode();
    sendResetRoomIdMessage(await getSocket(), getDefaultRoomId(user.id));
    this.setState({
      waitForConnectResponse: false,
      hasConversationStarted: false,
    });
  };

  handleTranscriptInputVisibilityChange() {
    const {
      newTranscriptInputVisibility,
      updateNewTranscriptInputVisibility,
    } = this.props;
    updateNewTranscriptInputVisibility(!newTranscriptInputVisibility);
  }

  download() {
    const { getTranscriptText } = this.props;
    getTranscriptText();
  }

  onNewTranscriptChange(ev) {
    const { scribeDeselectTranscriptWord } = this.props;
    scribeDeselectTranscriptWord();
    this.setState({ newTranscriptDraft: ev.target.value });
    ev.preventDefault();
    ev.stopPropagation();
  }

  submitNewTranscript(e) {
    const { newTranscriptDraft } = this.state;
    const {
      scribeCreateTranscript,
      user,
      scribeDeselectTranscriptWord,
    } = this.props;
    e.preventDefault();
    e.stopPropagation();
    this.setState({ newTranscriptDraft: '' });
    scribeDeselectTranscriptWord();
    scribeCreateTranscript(undefined, newTranscriptDraft, user.avaId);
    scribeDeselectTranscriptWord();
  }

  getCloseFabLocation() {
    const { electronCaptionMode, inTwilioCalls, muted } = this.props;
    if (
      (window.isElectron && !electronCaptionMode) ||
      // We also show CC button on Desktop web to make people DL the app
      (!window.isElectron && !isMobile())
    ) {
      if (inTwilioCalls || muted) {
        return 170;
      }
      return 350;
    }
    if (inTwilioCalls || muted) {
      return 80;
    }
    return 250;
  }

  openDesktopAppOrShowModal() {
    this.setState({ checkIfDesktopAppInstalled: true });
  }

  handleDesktopAppInstalledOrNot(isInstalled) {
    this.setState({ checkIfDesktopAppInstalled: false });
    // Let's show the desktop app popup
    // only if app is not installed
    if (!isInstalled) {
      this.setState({ showDesktopAppCaptionPopup: true });
    }
  }

  handleElectronCaptionMode(on: boolean) {
    const { theme, toggleSideBar, updateFullScreen } = this.props;
    const {
      previousWindowBounds,
    } = this.state;
    const { remote, ipcRenderer } = electron;
    const currentWindow = remote.getCurrentWindow();
    if (on) {
      const { bounds } = remote.screen.getPrimaryDisplay();
      const x = Math.round(bounds.x + ((bounds.width - 800)/2));
      const y = Math.round(bounds.y + ((bounds.height*1.6 - 160)/2));
      this.setState({
        previousWindowBounds: {
          x: currentWindow.getPosition()[0],
          y: currentWindow.getPosition()[1],
          width: currentWindow.getSize()[0],
          height: currentWindow.getSize()[1],
        },
        previousDarkMode: theme.palette.type === 'dark',
        themeHasSwitched: false,
      }, () => {
        toggleSideBar(false);
        updateFullScreen(true);
        currentWindow.setBounds({
          x,
          y,
          width: 800,
          height: 160,
        });
        ipcRenderer.send("setInCaptionMode", true);
        if (remote.process.platform === 'darwin') {
          currentWindow.setWindowButtonVisibility(false);
        }
      });
    } else {
      this.setState({
      }, () => {
        setTimeout(() => {
          updateFullScreen(false);
          currentWindow.setBounds({
            x: previousWindowBounds.x,
            y: previousWindowBounds.y,
            width: previousWindowBounds.width,
            height: previousWindowBounds.height,
          });
          ipcRenderer.send("setInCaptionMode", false);
          if (remote.process.platform === 'darwin') {
            currentWindow.setWindowButtonVisibility(true);
          }
        }, 0)
      });
    }
  }

  handleConversationEnd() {
    const { broadcastToZoomToken } = this.props;
    const { broadcastToZoomWarningShown } = this.state;
    // Let's mark if that conversation was broadcasted
    this.setState({
      conversationBroadcastedToZoom: !!broadcastToZoomToken,
    });
    if (!window.electron && broadcastToZoomToken && !broadcastToZoomWarningShown) {
      this.setState({
        broadcastToZoomWarningShown: true,
        showZoomBroadcastingWarning: true
      });
    } else {
      this.toggleConversationEndModal(true);
    }
  }

  toggleConversationEndModal(on: boolean = false) {
    const { updateBroadcastToZoomToken, updateElectronCaptionMode } = this.props;
    if (window.isElectron && on) {
      updateElectronCaptionMode(false);
    }
    updateBroadcastToZoomToken('');
    this.setState({
      roomEndingWarningBegin: 0,
      showConversationEndModal: on,
      showZoomBroadcastingWarning: false,
      broadcastToZoomWarningShown: false
    })
  }

  componentDidMount() {
    const { branch, token, scribe, toggleSideBar } = this.props;
    const request = localStorage.getItem('ava_request');
    if (branch && (branch.convoChannel || branch.roomId)) {
      this.onAvaName(`${branch.hostAvaId}`, {
        roomId: branch.convoChannel || branch.roomId,
        viaBranch: true,
      });
    } else if (token) {
      this.onAvaName(``, { token, scribe: !!scribe });
    } else if (request) {
      localStorage.removeItem('ava_request');
      this.onAvaName(`${request}`, {});
    }
    const containerElement = window.document.getElementById('gesture');
    const activeRegion = ZingTouch.Region(containerElement, false, false);
    activeRegion.bind(containerElement, 'swipe', event => {
      const { currentDirection } = event.detail.data[0];
      if (currentDirection < 20 || currentDirection > 340) {
        toggleSideBar(true);
      } else if (currentDirection > 160 && currentDirection < 200) {
        toggleSideBar(false);
      }
    });
  }

  async componentWillReceiveProps(nextProps: Props) {
    const {
      clearScribeConversationState,
      clearScribeSessionState,
      conversationEnded,
      conversationStarted,
      currentPlatform,
      displayable,
      electronCaptionMode,
      history,
      inConferenceCall,
      inTwilioCalls,
      isInConversation,
      lastMutationReceived: oldLastMutationReceived,
      lightView,
      mirror,
      recording,
      scribe,
      startRecord,
      stopRecord,
      socketConnected: oldSocketConnected,
      status: oldStatus,
      subscription,
      updateConversationEndedStatus,
      updateElectronCaptionMode,
      updateIsInConversation,
      updateNewTranscriptInputVisibility,
      user,
      toggleSideBar
    } = this.props;
    const {
      electronCaptionMode: nextElectronCaptionMode,
      socketConnected,
      status,
      inTwilioCalls: nextInTwilioCalls,
      lastMutationReceived,
      muted: nextMuted,
      recording: nextRecording,
    } = nextProps;
    if (nextElectronCaptionMode !== electronCaptionMode) {
      this.handleElectronCaptionMode(nextElectronCaptionMode);
      if (nextElectronCaptionMode) updateNewTranscriptInputVisibility(false);
    }
    if (
      !conversationStarted &&
      oldSocketConnected === 'CONNECTED' &&
      socketConnected === 'DISCONNECTED'
    ) {
      clearScribeConversationState();
      clearScribeSessionState();
      history.push('/scribe/login');
    }
    const { hasConversationStarted, showConversationEndModal } = this.state;
    let toReturn = false;
    if (status.id === undefined || !oldStatus.id) {
      toReturn = false;
    } else if (
      status.id !== oldStatus.id &&
      !isConversationEnded(status, user)
    ) {
      toReturn = true;
    }
    if (toReturn && !hasConversationStarted) {
      this.setState({ hasConversationStarted: true });
      if (conversationEnded) {
        updateConversationEndedStatus(false);
      }
    }
    if (
      status.state &&
      oldStatus.state &&
      status.state.value !== oldStatus.state.value &&
      status.state.value === 'ended' &&
      status.reason === 'room-ended-due-to-convo-restricted-session'
    ) {
      if (window.isElectron && electronCaptionMode) {
        updateElectronCaptionMode(false);
      }
      if (!showConversationEndModal) this.restartConversation();
    }

    if (conversationEnded) {
      this.setState({
        roomEndingWarningBegin: 0,
        roomEndingWarningCountdown: 0,
        roomEndingWarningProlongUrl: '',
      });
    }

    if (
      status.reason !== oldStatus.reason &&
      status.reason === 'convo-restricted-session-warning'
    ) {
      const { i18n } = this.props;
      const params = {
        lang: i18n.language,
        src: 'timeout',
        medium: window.isElectron ? 'desk' : 'web',
        mkt: ''
      };
      switch (subscription.ongoingSubscription) {
        case 'Campus':
          params.mkt = 'edu';
          break;
        case 'Pro':
          params.mkt = 'hr';
          break;
        case 'Premium':
        case 'Free':
          params.mkt = 'ind';
          break;
        default:
          break;
      }
      let url = `https://ava-me.typeform.com/to/q2n11h${Object.keys(params).reduce((acc, curr) => `${acc}${curr}=${params[curr]}&`, '?')}`;
      url = url.substring(0, url.length - 1);
      this.setState({
        roomEndingWarningBegin: Date.now(),
        roomEndingWarningCountdown: (status.restrictions.convoWarningCountdownTimerMs / 1000) || 300,
        roomEndingWarningProlongUrl: url,
      });
    }

    const prevIsInConversationValue = oldStatus && oldStatus.id && !isDefaultRoomId(oldStatus.id);
    const newIsInConversationValue = status.id && !isDefaultRoomId(status.id);
    const convoJustStarted = newIsInConversationValue !== isInConversation && newIsInConversationValue !== prevIsInConversationValue;
    if (convoJustStarted) {
      const screenWidth = Math.max((document.documentElement && document.documentElement.clientWidth) || 0, window.innerWidth || 0);
      if (newIsInConversationValue && screenWidth > 768 && !window.isElectron && !electronCaptionMode) {
        toggleSideBar(true);
      }
      if (newIsInConversationValue) {
        const socket = await getSocket();
        sendRoomStateUpdateMessage(socket, { value: 'ongoing' });
        sendUsePaidAsrUpdateMessage(socket, true);
      }
      updateIsInConversation(newIsInConversationValue);
    }
    const convoFlippedToPublic = (
      oldStatus.conversationMode === 'public-muted' &&
      status.conversationMode === 'public');
    if (
      (convoJustStarted || convoFlippedToPublic) &&
      currentPlatform === 'web' &&
      displayable.audio &&
      newIsInConversationValue &&
      !inConferenceCall &&
      !mirror &&
      !nextMuted &&
      !lightView &&
      !scribe &&
      !inTwilioCalls
    ) {
      startRecord();
    }
    if (recording && nextInTwilioCalls && !inTwilioCalls) {
      stopRecord();
    }
    // WARNING: Muting timer logic is tricky and error-prone, so be careful.
    // If we are not recording anymore - stop the muting timer, so that it does
    // not spill over into the next potential recording session.
    if (!nextRecording && recording) {
      this.stopMutingTimer();
    }
    const countParticipants = (participants) => {
      // For some reason backend's participants list contains duplicates.
      return participants ? new Set(participants.map(x => x.avaId)).size : 0;
    };
    // If there is more than one user - never mute the mic.
    if (countParticipants(status.participants) > 1) {
      this.stopMutingTimer();
    }
    // Restart the muting timer if there is a transcript mutation.
    if (lastMutationReceived !== oldLastMutationReceived) {
      this.startMutingTimer(stopRecord, true);
    }
    if (
      // The recording is beginning.
      !recording &&
      nextRecording &&
      !inTwilioCalls &&
      !inConferenceCall &&
      // Sometimes the status.participants list will be empty when we start
      // recording. But if there is more than one participant - the timer
      // will be reset after the list updates.
      countParticipants(status.participants) <= 1
    ) {
      // If we are starting to record, but it's not a twilio call and there is
      // only one user -> silence the mic after 100 seconds.
      this.startMutingTimer(stopRecord);
    }
    if (
      isInConversation &&
      isConversationEnded(status, user) &&
      !conversationEnded
    ) {
      updateConversationEndedStatus(true);
      if (window.isElectron && electronCaptionMode) {
        updateElectronCaptionMode(false);
      }
    }
    const { zoomExportPopup } = this.state;
    if (
      inTwilioCalls &&
      Object.values(status.twilioCalls).find(
        (call: any) => call.conferenceType === 'zoom'
      ) &&
      zoomExportPopup !== status.id &&
      zoomExportPopup !== 'display'
    ) {
      this.setState({ zoomExportPopup: 'display' });
    } else if (
      window.isElectron &&
      newIsInConversationValue &&
      !prevIsInConversationValue) {
      updateElectronCaptionMode(true);
    }
    if (isInConversation) {
      // Hide the journey banner
      branchSdk.closeJourney();
    } else {
      // Show the journey banner
      branchSdk.track('pageview');
    }
  }

  // Returns true is the timer was previously started.
  stopMutingTimer() {
    const timeoutSet = this.silenceTimeout !== undefined;
    if (timeoutSet) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = undefined;
    }
    return timeoutSet;
  }

  // If onlyRestart is set to true, will only start the timer if it was already
  // running. If onlyRestart is unset it will always start the timer anew.
  startMutingTimer(stopRecord, onlyRestart) {
    const timeoutSet = this.stopMutingTimer();
    if (onlyRestart && !timeoutSet) return;
    this.silenceTimeout = setTimeout(() => {
      stopRecord();
    }, 100000);
  }

  getEditRight() {
    const { intercomDisplayed } = this.props;
    return intercomDisplayed ? 170 : 90;
  }

  render() {
    const {
      acceptScribeConversationRequest,
      activeFeatures,
      backendSupport,
      classes,
      conversationEnded,
      conversationOrigin,
      conversationRequested,
      conversationStarted,
      createNewConversation,
      currentPlatform,
      displayable,
      electronCaptionMode,
      fontSize,
      fullScreen,
      getTranscriptStats,
      haveTranscripts,
      history,
      inConferenceCall,
      inTwilioCalls,
      insertSpeakerNameMode,
      isInConversation,
      lightView,
      loading,
      mirror,
      muted,
      newTranscriptInputVisibility,
      reconnectToWebSocket,
      scribe,
      socketConnected,
      status: newStatus,
      t,
      theme,
      updateAudioSourceMode,
      updateElectronCaptionMode,
      user,
    } = this.props;
    const status = newStatus;
    const {
      waitForConnectResponse,
      askingUserName,
      newTranscriptDraft,
      avaNameRequested,
      zoomExportPopup,
      previousDarkMode,
      roomEndingWarningBegin,
      roomEndingWarningCountdown,
      roomEndingWarningProlongUrl,
      showConversationEndModal,
      showZoomBroadcastingWarning,
      themeHasSwitched,
      checkIfDesktopAppInstalled,
      showDesktopAppCaptionPopup,
      conversationBroadcastedToZoom,
    } = this.state;

    let heightOffset = '';
    if (window.isElectron && !electronCaptionMode) {
      const { remote } = electron;
      if (remote.process.platform === 'darwin') {
        heightOffset = ' - 28px'
      } else {
        heightOffset = ' - 32px'
      }
    }

    if (loading) {
      return <Loading text={t('loading3d')} />;
    }
    return (
      <div>
        <SiteThemeContext.Consumer>
          {({ handleThemeChange, theme: currentTheme }) => {
            if (currentTheme === 'light' &&
              isInConversation &&
              window.isElectron &&
              electronCaptionMode) {
              handleThemeChange();
            } else if (currentTheme === 'dark' &&
              isInConversation &&
              window.isElectron &&
              !electronCaptionMode &&
              !previousDarkMode &&
              !themeHasSwitched) {
              handleThemeChange();
              this.setState({
                themeHasSwitched: true,
              });
            }
          }}
        </SiteThemeContext.Consumer>
        <audio id="player" controls muted style={{ display: 'none' }} />
        {!window.isElectron && (
          <ZoomBroadcastCaptionsPopupContainer
            on={zoomExportPopup === 'display'}
            setOff={() => {
              this.setState({ zoomExportPopup: status.id });
            }}
          />
        )}
        <div
          className={classes.container}
          style={{
            overflowX: 'hidden',
            position: 'relative',
            backgroundColor: theme.palette.background.default,
            height: `calc(var(--vh, 1vh) * 100${heightOffset})`,
            fontFamily: theme.typography.fontFamily,
            width: '100%',
          }}
        >
          <EditProfileModal
            open={askingUserName}
            close={e => this.handleEditProfilModalClose(e)}
            title={t('profilModal.enterYourName')}
            subtitle={`${t(
              'profilModal.weNeedToAnnouceYouTo'
            )} &${avaNameRequested || ''}`}
            defaultUserName=""
            action={t(`profilModal.continue`)}
          />
          {currentPlatform === 'web' &&
            !mirror &&
            !muted &&
            !lightView &&
            !scribe &&
            !fullScreen &&
            !inConferenceCall &&
            !inTwilioCalls &&
            isInConversation && (
              <>
                {newTranscriptInputVisibility && (
                  <form onSubmit={ev => this.submitNewTranscript(ev)}>
                    <Slide direction="left" in mountOnEnter unmountOnExit>
                      <Input
                        onChange={ev => this.onNewTranscriptChange(ev)}
                        // eslint-disable-next-line
                        value={this.state.newTranscriptDraft}
                        autoFocus
                        placeholder={t('conversation.typeTranscriptHere')}
                        onKeyDown={ev => {
                          if (['Enter'].includes(ev.key)) {
                            ev.preventDefault();
                            ev.stopPropagation();
                            this.submitNewTranscript(ev);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          bottom: 20,
                          right: this.getEditRight(),
                          width: `calc(100% - ${this.getEditRight() + 20}px)`,
                          zIndex: 1000,
                          fontSize,
                        }}
                      />
                    </Slide>
                  </form>
                )}
                {newTranscriptInputVisibility && newTranscriptDraft && (
                  <Fab
                    style={{
                      ...fabStyle(theme),
                      right: this.getEditRight() - 70,
                    }}
                    onClick={e => this.submitNewTranscript(e)}
                  >
                    <Send />
                  </Fab>
                )}
                <Fab
                  style={{
                    ...fabStyle(theme),
                    top: 170,
                    right: 15,
                    backgroundColor: newTranscriptInputVisibility ?
                      theme.palette.ava.blue
                      : theme.palette.ava.grey,
                  }}
                  onClick={() => this.handleTranscriptInputVisibilityChange()}
                >
                  <img src={Keyboard} alt="reply by keyboard" height="20" />
                </Fab>
              </>
            )}
          <span>
            {currentPlatform === 'scribe' && <ScribeSearchInputContainer />}
            {(currentPlatform === 'scribe' ||
              (socketConnected === 'CONNECTED' &&
                activeFeatures.includes('multi_edit_web'))) &&
              !muted &&
              !lightView && (
                // eslint-disable-next-line
                <ScribeKeypressHandlerContainer rights={currentPlatform} />
              )}
          </span>
          {currentPlatform === 'scribe' &&
            conversationStarted &&
            !conversationEnded && (
              <div
                style={{
                  width: '100%',
                  zIndex: 2000,
                  position: 'absolute',
                  marginTop: 20,
                  paddingLeft: 300,
                }}
              >
                <AudioChunkBufferContainer />
              </div>
            )}

          {currentPlatform === 'web' && !loading && (
            <Offline
              reconnect={reconnectToWebSocket}
              online={socketConnected === 'CONNECTED'}
              display={!lightView}
            />
          )}

          <SnackbarWithCountdown
            message={t('snackbar.freeLimitWarning')}
            countdown={roomEndingWarningCountdown}
            countdownBegin={roomEndingWarningBegin}
            additionalActionMessage={t('snackbar.freeLimitProlong')}
            additionalActionUrl={roomEndingWarningProlongUrl}
          />

          {currentPlatform === 'web' &&
            !loading &&
            !mirror &&
            !isInConversation && (
              <div
                style={{
                  marginTop: `calc(100px + 0.2 * (var(--vh, 1vh) * 100 - 320px))`,
                  maxWidth: 600,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center',
                }}
              >
                {!backendSupport.includes('connectRequest') &&
                  // eslint-disable-next-line
                  (console.log(
                    'redirect to because backend does not support connect request'
                  ) ||
                    1) &&
                  history.push('/download')}
                <UserSearchContainer
                  onAvaName={this.onAvaName}
                  host={user && user.features && user.features['web-host']}
                  onHost={createNewConversation}
                  twilio={user && user.features && user.features.twilio}
                />
                {waitForConnectResponse && (
                  <CircularProgress color="secondary" />
                )}
                <SwitchFooterContainer history={history} />
              </div>
            )}
          {currentPlatform === 'web' && mirror && !loading && !haveTranscripts && (
            <div
              style={{
                marginTop: 100,
                maxWidth: 600,
                marginLeft: 'auto',
                marginRight: 'auto',
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle1" style={{ fontSize: 18 }}>
                {t(
                  'conversation.speakInYourMobileAppAndCaptionsWillBeDisplayedHere'
                )}
                .
              </Typography>
              <SwitchFooterContainer history={history} />
            </div>
          )}
          {(currentPlatform === 'scribe' ||
            !status.participants ||
            isInConversation) && (
            <div
              style={{
                marginTop: fullScreen || lightView ? 0 : 67,
                display: 'flex',
              }}
            >
              {updateAudioSourceMode && (
                <div
                  style={{
                    backgroundColor: 'darksalmon',
                    width: '100%',
                    height: 30,
                    textAlign: 'center',
                  }}
                >
                  Update audio source mode
                </div>
              )}
              {insertSpeakerNameMode && (
                <div
                  style={{
                    backgroundColor: 'darksalmon',
                    width: '100%',
                    height: 30,
                    textAlign: 'center',
                  }}
                >
                  Insert Speaker Name mode
                </div>
              )}
              {scribe && status.scribe && status.scribe.status === 'leaving' && (
                <div
                  style={{
                    backgroundColor: 'darksalmon',
                    width: '100%',
                    height: 30,
                    textAlign: 'center',
                    padding: 5,
                  }}
                >
                  <span>{t('scribeEndRequested')}</span>
                </div>
              )}
              {conversationStarted ? (
                <ScribeTranscriptListContainer />
              ) : (
                <ConversationRequestMeta
                  acceptScribeConversationRequest={
                    acceptScribeConversationRequest
                  }
                  conversationOrigin={conversationOrigin}
                  conversationRequested={conversationRequested}
                  currentPlatform={currentPlatform}
                  loading={loading}
                  socketConnected={socketConnected}
                  theme={theme}
                />
              )}
              {displayable.audio && !muted && isInConversation && !scribe && (
                <AudioRecorderContainer
                  conversationEnded={isConversationEnded(status, user)}
                  pixelRight={this.getEditRight()}
                />
              )}
              <ScribeConversationOverModalContainer />
              {currentPlatform === 'web' &&
                status.participants &&
                !fullScreen &&
                !scribe &&
                !isDefaultRoomId(status.id) &&
                !isMobile() &&
                !window.isElectron && (
                  <>
                    <Fab
                      size="small"
                      style={{
                        ...fabStyle(theme),
                        width: 56,
                        height: 56,
                        backgroundColor: theme.palette.ava.navy,
                        top: inTwilioCalls ? 80 : 260,
                        right: 15,
                      }}
                      onClick={() => this.openDesktopAppOrShowModal()}
                    >
                      {' '}
                      <i
                        className="material-icons"
                        style={{ fontSize: '36px' }}
                      >
                        closed_caption
                      </i>
                    </Fab>
                    {checkIfDesktopAppInstalled && (
                      <DesktopAppInstalledComponent
                        desktopAppInstalledOrNot={isInstalled =>
                          this.handleDesktopAppInstalledOrNot(isInstalled)
                        }
                      />
                    )}
                    {showDesktopAppCaptionPopup && (
                      <DesktopAppCaptionsModal
                        onClose={() =>
                          this.setState({ showDesktopAppCaptionPopup: false })
                        }
                        t={t}
                      />
                    )}
                  </>
                )}
              {currentPlatform === 'web' &&
                status.participants &&
                !scribe &&
                !isDefaultRoomId(status.id) &&
                window.isElectron && (
                <>
                  {!electronCaptionMode && !fullScreen && (
                    <Fab
                      size="small"
                      style={{
                        ...fabStyle(theme),
                        width: 56,
                        height: 56,
                        backgroundColor: theme.palette.ava.navy,
                        top: inTwilioCalls ? 80 : 260,
                        right: 15,
                      }}
                      onClick={() => updateElectronCaptionMode(true)}
                    >
                      {' '}
                      <i className="material-icons" style={{ fontSize: '36px' }}>closed_caption</i>
                    </Fab>
                  )}
                  {electronCaptionMode && (
                    <div className={`electron-caption-mode-buttons ${classes.electronCaptionModeButtons}`}>
                      <button
                        className={classes.electronCaptionModeButton}
                        style={{
                          borderBottom: status.host && status.host.avaId === user.avaId ? 'solid 1px #333333' : '',
                        }}
                        type="button"
                        onClick={() => updateElectronCaptionMode(false)}>
                        <img src={ExpandIcon} alt="Expand" />
                      </button>
                      <button
                        className={classes.electronCaptionModeButton}
                        type="button"
                        onClick={() => this.handleConversationEnd()}>
                        <Close style={{ width: '1.2em', height: '1.2em', fill: '#EEEEEE' }} />
                      </button>
                    </div>
                  )}
                </>
              )}
              {currentPlatform === 'web' &&
                status.participants &&
                !scribe &&
                !fullScreen &&
                !isDefaultRoomId(status.id) && (
                  <>
                    <Tooltip
                      title={status.host && status.host.avaId === user.avaId ?
                        t('header.quitConversation')
                        : t('header.exitConversation')}
                      aria-label="quit-conversation"
                      enterDelay={300}
                      leaveDelay={200}
                    >
                      <Fab
                        size="small"
                        style={{
                          ...fabStyle(theme),
                          width: 56,
                          height: 56,
                          backgroundColor: 'rgba(255,0,0,1)',
                          top: this.getCloseFabLocation(),
                          right: 15,
                        }}
                        onClick={() => this.handleConversationEnd()}
                      >
                        {' '}
                        <Close />
                      </Fab>
                    </Tooltip>
                    {showConversationEndModal && (
                      <WebConversationOverModalContainer
                        restartConversation={() => {
                          this.toggleConversationEndModal(false);
                          this.restartConversation();
                        }}
                        download={() => this.download()}
                        getTranscriptStats={getTranscriptStats}
                        status={status}
                        user={user}
                        theme={theme}
                        endedByCurrentUser
                        conversationBroadcastedToZoom={conversationBroadcastedToZoom}
                      />
                    )}
                    <Dialog
                      open={showZoomBroadcastingWarning}
                      keepMounted
                      onClose={() => {}}
                      className={classes.zoomBroadcastWarningDialog}
                    >
                      <DialogContent>
                        <div className={classes.zoomBroadcastWarningIconWrap}>
                          <Typography className={classes.zoomBroadcastWarningIconText}>
                            CC
                          </Typography>
                        </div>
                        <Typography variant="h4" className={classes.zoomBroadcastWarningTitle} align="center" gutterBottom>
                          {t('conversation.zoomExportWarning.title')}
                        </Typography>
                        <Typography variant="body1" align="center" gutterBottom>
                          {t('conversation.zoomExportWarning.body1')}
                        </Typography>
                        <Typography variant="body1" align="center" gutterBottom>
                          {t('conversation.zoomExportWarning.body2')}
                        </Typography>
                      </DialogContent>
                      <DialogActions className={classes.zoomBroadcastWarningDialogActions}>
                        <Button
                          onClick={() => this.toggleConversationEndModal(true)}
                          className={classes.zoomBroadcastWarningButton}
                        >
                          {t('conversation.zoomExportWarning.confirm')}
                        </Button>
                        <Button
                          onClick={() => this.setState({ showZoomBroadcastingWarning: false })}
                          className={classes.zoomBroadcastWarningCancel}
                          variant="text"
                        >
                          {t('conversation.zoomExportWarning.cancel')}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              {user &&
                status &&
                status.id &&
                !scribe &&
                !lightView &&
                !isDefaultRoomId(status.id) &&
                isConversationEnded(status, user) && (
                  <div>
                    <Toggle>
                      {({ on, setOff }) => (
                        <WebConversationOverModalContainer
                          restartConversation={() => this.restartConversation()}
                          download={() => this.download()}
                          status={status}
                          user={user}
                          open={!on}
                          close={setOff}
                          theme={theme}
                          getTranscriptStats={getTranscriptStats}
                          conversationBroadcastedToZoom={conversationBroadcastedToZoom}
                        />
                      )}
                    </Toggle>
                  </div>
                )}
            </div>
          )}
          {currentPlatform === 'scribe' && <ScribeFooterMenu />}
          {lightView && false && <LightViewFooter />}
        </div>
      </div>
    );
  }
}

export default withTranslation()(withRouter(withTheme(withStyles(styles)(ScribeConversation))));
